import React from 'react';
import { Box, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material';
import { theme } from '../../../theme';
import { useTranslation } from 'react-i18next';

const Invoice: React.FC<{ invoiceData: any }> = ({ invoiceData }) => {
  const { t } = useTranslation();
  
  if (!invoiceData) return null;

  console.log('Invoice Data:', invoiceData);

  // Format date nicely
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDisplayValue = (value: any) => {
    if (typeof value === 'object' && value !== null && ('en' in value || 'ar' in value)) {
      return value.en || value.ar || '';
    }
    return value ?? '-';
  };

  // Safely derive monetary values
  const currency = (val: number | null | undefined) =>
    val != null && !Number.isNaN(Number(val)) ? `JOD ${Number(val).toFixed(2)}` : '-';

  const items = Array.isArray(invoiceData.orderItems) ? invoiceData.orderItems : [];

  const computeLine = (item: any) => {
    const quantity = Number(item?.quantity ?? 0);
    const unitPrice = Number(item?.unitPrice ?? item?.originalPrice ?? 0);
    // Support both discountedPrice and discountPrice field names
    const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
    const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
    const hasDiscount =
      discountedPrice != null &&
      discountedPrice > 0 &&
      discountedPrice < unitPrice;
    const effectiveUnit = hasDiscount ? discountedPrice : unitPrice;
    
    // Calculate choices total (choices are multiplied by quantity)
    const choices = Array.isArray(item.choices) ? item.choices : [];
    const choicesUnitTotal = choices.reduce((sum: number, choice: any) => {
      return sum + (Number(choice?.choicePrice ?? 0));
    }, 0);
    const choicesTotal = choicesUnitTotal * quantity;
    const mealPrice = item?.mealPrice != null ? Number(item.mealPrice) : 0;
    const mealChoiceGroups = Array.isArray(item?.mealChoiceGroups) ? item.mealChoiceGroups : [];
    const mealUnitTotal = mealPrice;
    const mealTotal = mealUnitTotal * quantity;

    const itemTotal = quantity * effectiveUnit;
    const lineTotal = itemTotal + choicesTotal + mealTotal;
    
    return { 
      quantity, 
      unitPrice, 
      discountedPrice, 
      effectiveUnit, 
      itemTotal,
      choicesUnitTotal,
      choicesTotal,
      mealPrice,
      mealChoiceGroups,
      mealUnitTotal,
      mealTotal,
      lineTotal 
    };
  };

  const computedSubTotal = items.reduce((sum: number, it: any) => sum + computeLine(it).lineTotal, 0);

  const inv = invoiceData?.invoiceDetails || {};
  const subTotal = inv?.subTotal != null ? Number(inv.subTotal) : computedSubTotal;
  const tax = inv?.tax != null ? Number(inv.tax) : 0;
  const serviceFee = inv?.serviceFee != null ? Number(inv.serviceFee) : 0;
  const deliveryFee = inv?.deliveryFee != null ? Number(inv.deliveryFee) : 0;
  const walletUsed = inv?.walletUsed != null ? Number(inv.walletUsed) : 0;
  const couponDiscount = inv?.couponDiscount != null ? Number(inv.couponDiscount) : 0;
  const offerDiscount = inv?.offerDiscount != null ? Number(inv.offerDiscount) : 0;
  const total = inv?.total != null
    ? Number(inv.total)
    : subTotal + tax + deliveryFee - walletUsed - couponDiscount - offerDiscount + serviceFee;

  return (
    <ThemeProvider theme={theme}>
      <Box
        style={{
          border: '1px solid #ddd',
          borderRadius: 5,
          padding: 16,
          background: '#fff',
          fontFamily: theme.typography?.fontFamily || 'inherit',
          fontSize: 14,
          lineHeight: 1.4,
        }}
      >
        <Box
          style={{
            borderBottom: '1px solid #eee',
            marginBottom: 16,
            paddingBottom: 8,
          }}
        >
          <Typography style={{ margin: 0, fontWeight: 600, fontSize: 18 }}>
            {t('orderManagement.invoice.invoiceDetails')}
          </Typography>
        </Box>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 16,
          }}
        >
          <Box>
            <Typography
              style={{
                fontSize: 20,
                color: '#333',
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              {t('orderManagement.invoice.to')}
            </Typography>
            <Typography style={{ fontWeight: 600 }}>
              {getDisplayValue(invoiceData.customerInfo?.name) || getDisplayValue(invoiceData.customerName)}
            </Typography>
            <Typography>
              {t('orderManagement.invoice.address')} {getDisplayValue(invoiceData.shippingAddress?.addressLine) || '-'}
            </Typography>
            <Typography>
              {t('orderManagement.invoice.phone')} {getDisplayValue(invoiceData.shippingAddress?.phoneNumber) || getDisplayValue(invoiceData.customerInfo?.phoneNumber) || '-'}
            </Typography>
            <Typography>
              {t('orderManagement.invoice.email')} {getDisplayValue(invoiceData.shippingAddress?.email) || getDisplayValue(invoiceData.customerInfo?.email) || '-'}
            </Typography>
          </Box>
          <Box style={{ minWidth: 320 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
              <tbody>
                <tr>
                  <td style={{ paddingRight: 12, whiteSpace: 'nowrap', color: '#555', fontWeight: 600, verticalAlign: 'middle' }}>{t('orderManagement.invoice.orderPlacedOn')}</td>
                  <td style={{ textAlign: 'right', color: '#333', verticalAlign: 'middle' }}>{formatDate(invoiceData.orderDate)}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 12, whiteSpace: 'nowrap', color: '#555', fontWeight: 600, verticalAlign: 'middle' }}>{t('orderManagement.invoice.orderId')}</td>
                  <td style={{ textAlign: 'right', color: '#333', verticalAlign: 'middle' }}>{invoiceData.orderNumber}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 12, whiteSpace: 'nowrap', color: '#555', fontWeight: 600, verticalAlign: 'middle' }}>{t('orderManagement.invoice.paymentStatus')}</td>
                  <td style={{ textAlign: 'right', color: '#333', textTransform: 'capitalize', verticalAlign: 'middle' }}>{invoiceData.paymentStatus || '-'}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 12, whiteSpace: 'nowrap', color: '#555', fontWeight: 600, verticalAlign: 'middle' }}>{t('orderManagement.invoice.paymentMethod')}</td>
                  <td style={{ textAlign: 'right', color: '#333', textTransform: 'capitalize', verticalAlign: 'middle' }}>{invoiceData.paymentMethod || '-'}</td>
                </tr>
              </tbody>
            </table>
          </Box>
        </Box>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: 24,
            background: '#fafafa',
          }}
        >
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th
                style={{
                  padding: 8,
                  border: '1px solid #eee',
                  textAlign: 'left',
                  fontWeight: 600,
                  verticalAlign: 'middle',
                }}
              >
                <Typography style={{ fontWeight: 500 }}>
                  {t('orderManagement.invoice.itemName')}
                </Typography>
              </th>
              <th
                style={{
                  padding: 8,
                  border: '1px solid #eee',
                  textAlign: 'center',
                  fontWeight: 600,
                  verticalAlign: 'middle',
                }}
              >
                <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.quantity')}</Typography>
              </th>
              <th
                style={{
                  padding: 8,
                  border: '1px solid #eee',
                  textAlign: 'right',
                  fontWeight: 600,
                  verticalAlign: 'middle',
                }}
              >
                <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.unitPrice')}</Typography>
              </th>
              <th
                style={{
                  padding: 8,
                  border: '1px solid #eee',
                  textAlign: 'right',
                  fontWeight: 600,
                  verticalAlign: 'middle',
                }}
              >
                <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.subTotal')}</Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item: any, idx: number) => {
              const { quantity, itemTotal, lineTotal, effectiveUnit, mealPrice, mealChoiceGroups, mealUnitTotal, mealTotal } = computeLine(item);
              const choices = Array.isArray(item.choices) ? item.choices : [];
              const hasMeal = Boolean(item?.mealId || item?.mealName || mealPrice > 0 || (mealChoiceGroups && mealChoiceGroups.length > 0));
              
              return (
                <React.Fragment key={idx}>
                  {/* Main Item Row */}
                  <tr style={{ backgroundColor: '#f9f9f9' }}>
                    <td style={{ padding: 12, border: '1px solid #ddd', fontWeight: 500, verticalAlign: 'middle' }}>
                      <Typography style={{ fontWeight: 500, fontSize: '16px' }}>
                        {getDisplayValue(item.itemName) || '-'}
                      </Typography>
                    </td>
                    <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'center', verticalAlign: 'middle' }}>
                      <Typography style={{ fontWeight: 500 }}>{quantity || '-'}</Typography>
                    </td>
                    <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'right', verticalAlign: 'middle' }}>
                      <Typography style={{ fontWeight: 500 }}>
                        {currency(effectiveUnit)}
                      </Typography>
                    </td>
                    <td style={{ padding: 12, border: '1px solid #ddd', textAlign: 'right', verticalAlign: 'middle' }}>
                      <Typography style={{ fontWeight: 500 }}>{currency(itemTotal)}</Typography>
                    </td>
                  </tr>
                  
                  {/* Choices/Add-ons */}
                  {choices.map((choice: any, choiceIdx: number) => {
                    const choiceTotal = Number(choice.choicePrice ?? 0) * quantity;
                    return (
                      <tr key={`${idx}-choice-${choiceIdx}`} style={{ backgroundColor: '#fafafa' }}>
                        <td style={{ 
                          padding: '8px 12px 8px 32px', 
                          border: '1px solid #ddd', 
                          fontSize: '14px',
                          borderLeft: '3px solid #e0e0e0'
                        }}>
                          <Typography style={{ 
                            fontStyle: 'italic',
                            color: '#666',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <span style={{ 
                              marginRight: '8px',
                              color: '#4caf50',
                              fontWeight: 'bold'
                            }}>+</span>
                            {getDisplayValue(choice.choiceName) || '-'}
                          </Typography>
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', verticalAlign: 'middle' }}>
                          <Typography style={{ color: '#999' }}>{quantity}</Typography>
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'right', verticalAlign: 'middle' }}>
                          <Typography style={{ color: '#666' }}>{currency(choice.choicePrice)}</Typography>
                        </td>
                        <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'right', verticalAlign: 'middle' }}>
                          <Typography style={{ color: '#666', fontWeight: 500 }}>
                            {currency(choiceTotal)}
                          </Typography>
                        </td>
                      </tr>
                    );
                  })}
                  
                  {/* Meal */}
                  {hasMeal && (
                    <tr style={{ backgroundColor: '#eef5ff' }}>
                      <td style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        fontSize: '14px',
                        borderLeft: '3px solid #90caf9'
                      }}>
                        <Typography style={{ 
                          fontStyle: 'italic',
                          color: '#1565c0',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <span style={{ marginRight: '8px' }}>🍽️</span>
                          {t('orderManagement.invoice.meal')}: {getDisplayValue(item.mealName) || '-'}
                        </Typography>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', verticalAlign: 'middle' }}>
                        <Typography style={{ color: '#666' }}>{quantity}</Typography>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'right', verticalAlign: 'middle' }}>
                        <Typography style={{ color: '#1565c0' }}>{currency(mealUnitTotal)}</Typography>
                      </td>
                      <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'right', verticalAlign: 'middle' }}>
                        <Typography style={{ color: '#1565c0', fontWeight: 500 }}>
                          {currency(mealTotal)}
                        </Typography>
                      </td>
                    </tr>
                  )}

                  {/* Meal Choice Groups */}
                  {hasMeal && mealChoiceGroups.map((group: any, groupIdx: number) => (
                    <React.Fragment key={`${idx}-meal-group-${groupIdx}`}>
                      {(Array.isArray(group?.choices) ? group.choices : []).map((choice: any, choiceIdx: number) => {
                        return (
                          <tr key={`${idx}-meal-choice-${groupIdx}-${choiceIdx}`} style={{ backgroundColor: '#f3f8ff' }}>
                            <td style={{
                              padding: '8px 12px 8px 32px',
                              border: '1px solid #ddd',
                              fontSize: '13px',
                              borderLeft: '3px solid #bbdefb'
                            }}>
                              <Typography style={{
                                fontStyle: 'italic',
                                color: '#5c6f7b',
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                <span style={{ marginRight: '8px', color: '#90caf9', fontWeight: 'bold' }}>+</span>
                                {getDisplayValue(group?.choiceGroupName) || t('orderManagement.invoice.mealChoiceGroup')}: {getDisplayValue(choice?.choiceName) || '-'}
                              </Typography>
                            </td>
                            <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'center', verticalAlign: 'middle' }}>
                              <Typography style={{ color: '#999' }}>{quantity}</Typography>
                            </td>
                            <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'right', verticalAlign: 'middle' }}>
                              <Typography style={{ color: '#666' }}>{currency(0)}</Typography>
                            </td>
                            <td style={{ padding: '8px 12px', border: '1px solid #ddd', textAlign: 'right', verticalAlign: 'middle' }}>
                              <Typography style={{ color: '#666', fontWeight: 500 }}>
                                {currency(0)}
                              </Typography>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  
                  {/* Item Total Row (only show if there are choices) */}
                  {(choices.length > 0 || hasMeal) && (
                    <tr style={{  borderTop: '2px solid #ddd' }}>
                      <td colSpan={3} style={{ 
                        padding: '10px 12px', 
                        border: '1px solid #ddd',
                        textAlign: 'right',
                        fontWeight: 600
                      }}>
                        <Typography style={{ fontWeight: 600 }}>
                          {t('orderManagement.invoice.subtotalFor')} {getDisplayValue(item.itemName)}:
                        </Typography>
                      </td>
                      <td style={{ 
                        padding: '10px 12px', 
                        border: '1px solid #ddd',
                        textAlign: 'right'
                      }}>
                        <Typography style={{ fontWeight: 600, fontSize: '16px' }}>
                          {currency(lineTotal)}
                        </Typography>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <table
            style={{
              width: '50%',
              minWidth: 480,
              borderCollapse: 'collapse',
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th colSpan={2} style={{ 
                  padding: 14, 
                  textAlign: 'center',
                  borderBottom: '1px solid #ddd'
                }}>
                  <Typography style={{ fontWeight: 600, fontSize: '18px', color: '#333' }}>
                    {t('orderManagement.invoice.orderSummary')}
                  </Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    padding: 12,
                    border: '1px solid #eee',
                    fontWeight: 500,
                    color: '#555',
                  }}
                >
                  <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.itemsTotal')}</Typography>
                </td>
                <td
                  style={{
                    padding: 12,
                    border: '1px solid #eee',
                    textAlign: 'right',
                  }}
                >
                  <Typography style={{ fontWeight: 500 }}>{currency(subTotal)}</Typography>
                </td>
              </tr>
              {tax > 0 && (
                <tr>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      fontWeight: 500,
                      color: '#555',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.tax')}</Typography>
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      textAlign: 'right',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>{currency(tax)}</Typography>
                  </td>
                </tr>
              )}
              {deliveryFee > 0 && (
                <tr>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      fontWeight: 500,
                      color: '#555',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.deliveryFee')}</Typography>
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      textAlign: 'right',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>{currency(deliveryFee)}</Typography>
                  </td>
                </tr>
              )}
              {serviceFee > 0 && (
                <tr>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      fontWeight: 500,
                      color: '#555',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.serviceFee')}</Typography>
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      textAlign: 'right',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>{currency(serviceFee)}</Typography>
                  </td>
                </tr>
              )}
              {couponDiscount > 0 && (
                <tr>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      fontWeight: 500,
                      color: '#555',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.couponDiscount')}</Typography>
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      textAlign: 'right',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>
                      -{currency(couponDiscount)}
                    </Typography>
                  </td>
                </tr>
              )}
              {offerDiscount > 0 && (
                <tr>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      fontWeight: 500,
                      color: '#555',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>{t('orderManagement.invoice.offerDiscount')}</Typography>
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: '1px solid #eee',
                      textAlign: 'right',
                    }}
                  >
                    <Typography style={{ fontWeight: 500 }}>
                      -{currency(offerDiscount)}
                    </Typography>
                  </td>
                </tr>
              )}
              <tr
                style={{
                  background: '#f5f5f5',
                  fontWeight: 600,
                }}
              >
                <td
                  style={{
                    padding: 14,
                    border: '1px solid #ddd',
                  }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '16px' }}>
                    {t('orderManagement.invoice.totalAmount')}
                  </Typography>
                </td>
                <td
                  style={{
                    padding: 14,
                    border: '1px solid #ddd',
                    textAlign: 'right',
                  }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '18px' }}>
                    {currency(total)}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
        <Box style={{ marginTop: 32 }}>
          <button
            onClick={() => window.print()}
            style={{
              padding: '8px 24px',
              border: '1px solid #bbb',
              borderRadius: 4,
              background: '#fff',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <span role="img" aria-label="print" style={{ marginRight: 8 }}>
              🖨️
            </span>
            {t('orderManagement.invoice.print')}
          </button>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Invoice;
